import React, { useCallback, useEffect, useState } from "react";

import MqttSuspectedBanner from "./tickets/MqttSuspectedBanner";
import ReaderToolsPanel from "./tickets/ReaderToolsPanel";
import TicketPhotoThumb from "./tickets/TicketPhotoThumb";
import TicketPhotoUpload from "./tickets/TicketPhotoUpload";
import { Button } from "./ui/button";
import TroubleshootWizard, {
  type PathEntry,
} from "./troubleshooting/TroubleshootWizard";
import { useAuth } from "../contexts/AuthContext";
import {
  claimTicket,
  deleteTicketPhoto,
  fetchTicketAssignee,
  fetchTicketComments,
  mttrResolveSourceTicket,
  postTicketComment,
  releaseTicket,
  TicketApiError,
  uploadTicketPhoto,
  type AssigneeResponse,
  type TicketComment,
} from "../services/ticketsApi";
import { fmtAgo, fmtUtc } from "../lib/format";
import type { Box } from "../types/boxes";

type WorkType = "remote" | "onsite" | "note";
type TicketOrigin = "source" | "_jcox";

interface TicketActivityProps {
  origin: TicketOrigin;
  ticketId: number;
  /** Hint from boxes.json so we can render the assignee instantly,
   *  before the API resolves. The actual source of truth is the API. */
  initialAssignee?: string | null;
  /** Full Box payload — drives the troubleshooting wizard's auto-answers. */
  box: Box;
}

const WORK_TYPE_LABEL: Record<WorkType, string> = {
  remote: "Remote work",
  onsite: "Onsite visit",
  note: "Note (no work)",
};

const PILL_CLASSES: Record<TicketComment["work_type"], string> = {
  remote: "bg-primary-100 text-primary-700",
  onsite: "bg-warning-100 text-warning-700",
  note: "bg-secondary-200 text-secondary-700",
  status: "bg-secondary-100 text-secondary-500 italic",
};

export const TicketActivity: React.FC<TicketActivityProps> = ({
  origin,
  ticketId,
  initialAssignee,
  box,
}) => {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [comments, setComments] = useState<TicketComment[] | null>(null);
  const [assigneeResp, setAssigneeResp] = useState<AssigneeResponse | null>(
    initialAssignee !== undefined && initialAssignee !== null
      ? {
          ticket_origin: origin,
          ticket_id: ticketId,
          assignee: initialAssignee,
          assigned_utc: null,
        }
      : null,
  );
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [draftType, setDraftType] = useState<WorkType>("remote");

  // Identity comes from the login now — no free-text name field. Claiming,
  // commenting, and resolving all attribute to the signed-in display name.
  const { user, can } = useAuth();
  const author = (user?.display_name ?? "").trim();
  // Mirrors the server rule: the uploader, or an admin, may remove a photo.
  const canDeletePhoto = (uploaderUid: number | null): boolean =>
    can("users.manage") || (uploaderUid != null && uploaderUid === user?.user_id);

  const refresh = useCallback(async () => {
    setLoadingError(null);
    try {
      const [cs, a] = await Promise.all([
        fetchTicketComments(origin, ticketId),
        fetchTicketAssignee(origin, ticketId),
      ]);
      setComments(cs.comments);
      setAssigneeResp(a);
    } catch (e) {
      const msg =
        e instanceof TicketApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "unknown error";
      setLoadingError(msg);
      // Keep whatever we had; don't blank out the UI on a transient failure.
    }
  }, [origin, ticketId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onSubmitComment = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    const body = draftBody.trim();
    if (!author) {
      setLoadingError("Your login has no display name; can't attribute the comment.");
      return;
    }
    if (!body) {
      setLoadingError("Comment body is empty.");
      return;
    }
    setBusy(true);
    setLoadingError(null);
    try {
      await postTicketComment(origin, ticketId, {
        author,
        work_type: draftType,
        body,
      });
      setDraftBody("");
      await refresh();
    } catch (e) {
      const msg =
        e instanceof TicketApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "unknown error";
      setLoadingError(msg);
    } finally {
      setBusy(false);
    }
  };

  const onClaim = async (): Promise<void> => {
    if (!author) {
      setLoadingError("Your login has no display name; can't claim.");
      return;
    }
    setBusy(true);
    setLoadingError(null);
    try {
      await claimTicket(origin, ticketId, author);
      await refresh();
    } catch (e) {
      const msg =
        e instanceof TicketApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "unknown error";
      setLoadingError(msg);
    } finally {
      setBusy(false);
    }
  };

  const onMttrResolve = async (): Promise<void> => {
    if (origin !== "source") return;
    if (!author) {
      setLoadingError("Your login has no display name; can't resolve.");
      return;
    }
    if (
      !window.confirm(
        "Mark this source ticket as MTTR-resolved?\n\n" +
          "This writes an overlay row in _jcox only — the source ticket " +
          "stays open in ops's view. Use this when the box has been " +
          "healthy long enough that you're confident the ticket no " +
          "longer represents an active issue.",
      )
    ) {
      return;
    }
    setBusy(true);
    setLoadingError(null);
    try {
      await mttrResolveSourceTicket(ticketId, author);
      await refresh();
    } catch (e) {
      const msg =
        e instanceof TicketApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "unknown error";
      setLoadingError(msg);
    } finally {
      setBusy(false);
    }
  };

  const onRelease = async (): Promise<void> => {
    setBusy(true);
    setLoadingError(null);
    try {
      await releaseTicket(origin, ticketId, author);
      await refresh();
    } catch (e) {
      const msg =
        e instanceof TicketApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "unknown error";
      setLoadingError(msg);
    } finally {
      setBusy(false);
    }
  };

  const currentAssignee = assigneeResp?.assignee ?? null;
  const isMine =
    currentAssignee !== null && currentAssignee === author && author !== "";

  return (
    <div className="bg-white border border-secondary-200 rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-secondary-800">
          Ticket activity
        </h2>
        <div className="text-xs text-secondary-500">
          {origin} #{ticketId}
        </div>
      </div>

      <MqttSuspectedBanner box={box} />

      {/* Reader control — test scan, force scan, and the record of what has
          already been tried on this box. Operator-gated inside. */}
      <ReaderToolsPanel scuName={box.scu_name} />

      {/* Assignee + claim/release */}
      <div className="flex items-center gap-2 mb-3">
        <div className="text-sm">
          <span className="text-secondary-500">Assigned to: </span>
          {currentAssignee ? (
            <strong className="text-primary-700">{currentAssignee}</strong>
          ) : (
            <em className="text-secondary-500">unclaimed</em>
          )}
          {assigneeResp?.assigned_utc && (
            <span className="text-xs text-secondary-500 ml-1">
              ({fmtAgo(assigneeResp.assigned_utc)})
            </span>
          )}
        </div>
        <div className="ml-auto flex gap-1">
          {!isMine && (
            <Button
              type="button"
              size="sm"
              variant="default"
              disabled={busy || !author}
              onClick={() => void onClaim()}
              title={`Claim as ${author || "(no display name)"}`}
            >
              {currentAssignee ? "Take over" : "Claim"}
            </Button>
          )}
          {currentAssignee !== null && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void onRelease()}
            >
              Release
            </Button>
          )}
          {origin === "source" && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy || !author}
              onClick={() => void onMttrResolve()}
              title="Marks the source ticket MTTR-resolved (writes overlay row in _jcox; source DB untouched)"
              className="text-success-700 border-success-300"
            >
              Mark MTTR-resolved
            </Button>
          )}
        </div>
      </div>

      {/* Identity comes from the login — claims, comments, and resolutions are
          all attributed to the signed-in user automatically. */}
      <div className="mb-3 text-xs text-secondary-500">
        Posting as{" "}
        {author ? (
          <strong className="text-secondary-800">{author}</strong>
        ) : (
          <em className="text-error-600">your login has no display name</em>
        )}
      </div>

      {/* Error banner */}
      {loadingError && (
        <div className="mb-3 text-xs text-error-700 bg-error-50 px-2 py-1 rounded">
          {loadingError}
        </div>
      )}

      {/* Troubleshooting wizard launcher + panel. Each step's answer is
          posted as a 'status' comment with a "[TS]" body prefix so the
          comment stream serves as the audit trail. */}
      <div className="mb-3 border border-primary-200 rounded bg-primary-50/30">
        <button
          type="button"
          onClick={() => setWizardOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-primary-800 hover:bg-primary-50"
          aria-expanded={wizardOpen}
        >
          <span>🛠 Troubleshooting walkthrough</span>
          <span>{wizardOpen ? "▾" : "▸"}</span>
        </button>
        {wizardOpen && (
          <div className="p-3 border-t border-primary-200">
            <TroubleshootWizard
              box={box}
              onAdvance={async (entry: PathEntry) => {
                const lines = [
                  `[TS] ${entry.nodeId}`,
                  entry.answer ? `  → ${entry.answer}` : null,
                  entry.auto ? `  (auto: ${entry.autoSource ?? "MTTR"})` : null,
                ].filter(Boolean);
                try {
                  await postTicketComment(origin, ticketId, {
                    author: author || "operator",
                    work_type: "note",
                    body: lines.join("\n"),
                  });
                  await refresh();
                } catch (e) {
                  setLoadingError(
                    e instanceof Error ? e.message : "failed to log step",
                  );
                }
              }}
              onFinish={() => setWizardOpen(false)}
              onAddPhoto={async (nodeId, image, caption) => {
                await uploadTicketPhoto(origin, ticketId, { image, caption, nodeId });
                await refresh();
              }}
            />
          </div>
        )}
      </div>

      {/* Comment list */}
      <div className="mb-3 max-h-96 overflow-y-auto border border-secondary-100 rounded">
        {comments === null ? (
          <div className="p-3 text-xs text-secondary-500">loading…</div>
        ) : comments.length === 0 ? (
          <div className="p-3 text-xs text-secondary-500">
            No comments yet. Be the first to log work.
          </div>
        ) : (
          <ul className="divide-y divide-secondary-100">
            {comments.map((c) => (
              <li key={c.comment_id} className="p-3">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-mono font-semibold text-primary-700">
                    {c.author}
                  </span>
                  <span
                    className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase font-medium ${PILL_CLASSES[c.work_type]}`}
                  >
                    {c.work_type}
                  </span>
                  <span
                    className="text-secondary-500 ml-auto"
                    title={fmtUtc(c.posted_utc) + " UTC"}
                  >
                    {fmtAgo(c.posted_utc)}
                  </span>
                </div>
                <div className="text-sm text-secondary-800 mt-1 whitespace-pre-wrap">
                  {c.body}
                </div>
                {c.photos && c.photos.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {c.photos.map((ph) => (
                      <TicketPhotoThumb
                        key={ph.photo_id}
                        origin={origin}
                        ticketId={ticketId}
                        photo={ph}
                        onDelete={
                          canDeletePhoto(ph.uploaded_by_uid)
                            ? async () => {
                                await deleteTicketPhoto(origin, ticketId, ph.photo_id);
                                await refresh();
                              }
                            : undefined
                        }
                      />
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add a comment */}
      <form onSubmit={(e) => void onSubmitComment(e)} className="space-y-2">
        <div className="flex gap-3 text-xs text-secondary-700">
          {(["remote", "onsite", "note"] as WorkType[]).map((wt) => (
            <label key={wt} className="inline-flex items-center gap-1">
              <input
                type="radio"
                name="work_type"
                value={wt}
                checked={draftType === wt}
                onChange={() => setDraftType(wt)}
              />
              {WORK_TYPE_LABEL[wt]}
            </label>
          ))}
        </div>
        <textarea
          value={draftBody}
          onChange={(e) => setDraftBody(e.target.value)}
          placeholder="What was done? Findings? Next steps?"
          rows={3}
          className="w-full text-sm border border-secondary-300 rounded px-2 py-1 font-sans"
        />
        <div className="flex flex-wrap items-start justify-between gap-2">
          <TicketPhotoUpload
            disabled={!author}
            onUpload={async (image, caption) => {
              await uploadTicketPhoto(origin, ticketId, {
                image, caption, workType: draftType,
              });
              await refresh();
            }}
          />
          <Button
            type="submit"
            size="sm"
            disabled={busy || !draftBody.trim() || !author}
          >
            {busy ? "posting…" : "Post comment"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TicketActivity;
